import { skipToken } from "@reduxjs/toolkit/query";
import {
  collectPlaybookCriteria,
  playbookIdSchema,
} from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  teammateIdSchema,
} from "@repo/app-types/entities/teammate";
import type { FC } from "react";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { renderCrumbLabel } from "../../app/navigation/crumbLabel.js";

// Breadcrumb labels for the performance workspace (the playbook name label is
// also used by the settings playbook editor).

export const TeammateNameCrumbLabel: FC<{
  teammateId: string | undefined;
}> = ({ teammateId }) => {
  const parsedId = teammateIdSchema.safeParse(teammateId);
  const result = useBasicTeammateQuery(
    parsedId.success ? parsedId.data : skipToken,
  );
  return renderCrumbLabel({
    hasValidId: parsedId.success,
    result,
    buildText: (teammate) =>
      buildTeammateDisplayNames(teammate).primaryDisplayName,
    fallbackText: "Teammate",
  });
};

export const PlaybookNameCrumbLabel: FC<{
  playbookId: string | undefined;
}> = ({ playbookId }) => {
  const parsedId = playbookIdSchema.safeParse(playbookId);
  const result = usePlaybookByIdQuery(
    parsedId.success ? parsedId.data : skipToken,
  );
  return renderCrumbLabel({
    hasValidId: parsedId.success,
    result,
    buildText: (playbook) => playbook.name,
    fallbackText: "Playbook",
  });
};

export const PlaybookCriterionNameCrumbLabel: FC<{
  playbookId: string | undefined;
  criterionId: string | undefined;
}> = ({ playbookId, criterionId }) => {
  const parsedId = playbookIdSchema.safeParse(playbookId);
  const result = usePlaybookByIdQuery(
    parsedId.success ? parsedId.data : skipToken,
  );
  return renderCrumbLabel({
    hasValidId: parsedId.success,
    result,
    buildText: (playbook) =>
      collectPlaybookCriteria(playbook.assessment_tree).find(
        (node) => node.id === criterionId,
      )?.name ?? "Criterion",
    fallbackText: "Criterion",
  });
};
